import React, { useEffect, useState } from "react";
import {
  FieldArray,
  FormikProvider,
  useFormik,
  Field,
  Form,
  ErrorMessage,
} from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { config } from "../../CommonUtils/CommonURLS";
import { CommonAxiosGet, CommonAxiosPost } from "../../CommonUtils/CommonAxios";
import * as jnb from "react-bootstrap";

function IntakeEnhancement({ type, courseData }) {
  const [showEditTable, setShowEditTable] = useState(false);
  const [enhanceStatus, setEnhanceStatus] = useState(false);

  const validationSchema = Yup.object({
    intakeDetails: Yup.array().of(
      Yup.object({
        change_value: Yup.number()
          .typeError("Must be a number")
          .required("Required"),
        aicte_ref: Yup.string().required("Required"),
      }),
    ),
  });
  const formik = useFormik({
    initialValues: {
      selectedCourses: [],
      intakeDetails: [
        {
          course_name: "",
          course_id: "",
          intake: "",
          change_value: "",
          effective: "",
          aicte_ref: "",
          sno: "",
          course_cat_name: "",
          course_cat_id: "",
          course_group_desc: "",
          course_group_code: "",
        },
      ],
    },
    validationSchema,
    onSubmit: (values) => {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to submit the intake changes?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, submit it!",
      }).then((result) => {
        if (result.isConfirmed) {
          const payload = {
            intakeType: type,
            intakelist: values.intakeDetails.map((item) => ({
              courseId: parseInt(item.course_id),
              sanctionIntake: parseInt(item.intake) || 0,
              change: item.change_value,
              effectiveIntake:
                type === "enhance"
                  ? (parseInt(item.intake) || 0) +
                  (parseInt(item.change_value) || 0)
                  : (parseInt(item.intake) || 0) -
                  (parseInt(item.change_value) || 0),

              nature: type === "enhance" ? "Increase" : "Reduction",
              aicteRef: item.aicte_ref,
              courseGropcode: parseInt(item.course_group_code),
              courseType: parseInt(item.course_cat_id),
            })),
          };
          const url = `${config.url.FAC_URL}saveIntakeChanges`;
          CommonAxiosPost(url, payload)
            .then((res) => {
              if (res?.data?.scode === "01") {
                Swal.fire({
                  title: res.data.sdesc || "Intake Details Saved!",
                  icon: "success",
                  backdrop: false,
                  confirmButtonText: "OK",
                }).then((successResult) => {
                  if (successResult.isConfirmed) {
                    GetIntakeDetails();
                  }
                });
              } else {
                Swal.fire({
                  text: res.data?.sdesc || "Failed to save details",
                  icon: "warning",
                });
              }
            })
            .catch((err) => {
              console.error("Submission Error:", err);
              Swal.fire({
                text: "An error occurred during submission.",
                icon: "error",
              });
            });
        }
      });
    },
  });
  //  ********** field array get api************
  const GetIntakeDetails = () => {
    const intakeType = type === "enhance" ? "Increase" : "Reduction";
    const url = `${config.url.FAC_URL}getInputDetails/${intakeType}`;
    CommonAxiosGet(url)
      .then((res) => {
        if (res?.data?.enhancement_status) {
          setShowEditTable(true);
          setEnhanceStatus(res?.data?.enhancement_status);
          const mappedData = res?.data?.enhancement?.map((item) => {
            const course = courseData.find(c => c.course_id === item.course_id);
            return {
              sno: item.sno,
              course_id: item.course_id,
              course_name: course?.course_name || "",
              intake: item.sanction_intake,
              change_value: item.change,
              effective:
                type === "enhance"
                  ? (Number(item.sanction_intake) || 0) + (Number(item.change) || 0)
                  : (Number(item.sanction_intake) || 0) - (Number(item.change) || 0),
              aicte_ref: item.aicte_ref,
              course_cat_id: course?.course_cat_id || "",
              course_cat_name: course?.course_cat_name || "",
              course_group_code: item.course_group_code,
              course_group_desc: course?.course_group_desc || "",
            };
          });

          formik.setValues({
            ...formik.values,
            intakeDetails: mappedData,
            selectedCourses: res?.data?.enhancement?.map(item => item.sno),
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching intake details:", err);
      });
  };

  useEffect(() => {
    if (courseData.length === 0) return; // ✅ WAIT until data is loaded

    setShowEditTable(false);
    setEnhanceStatus(false);

    formik.setValues({
      selectedCourses: [],
      intakeDetails: [
        {
          course_id: "",
          course_name: "",
          intake: "",
          change_value: "",
          effective: "",
          aicte_ref: "",
          sno: "",
          course_cat_id: "",
          course_cat_name: "",
          course_group_code: "",
          course_group_desc: "",
        },
      ],
    });

    GetIntakeDetails();
  }, [type, courseData]); // ✅ ADD courseData here

  const { values, setFieldValue } = formik;

  /* SELECT ALL */
  const handleSelectAll = (e) => {
    if (enhanceStatus) {
      Swal.fire({ icon: "warning", text: "Already Edited" });
      return;
    } else {
      setShowEditTable(false);
      if (e.target.checked) {
        const allIds = courseData.map((c) => c.sno);
        setFieldValue("selectedCourses", allIds);
      } else {
        setFieldValue("selectedCourses", []);
      }
    }
  };

  /* ROW SELECT */
  const handleRowSelect = (sno, checked) => {
    if (enhanceStatus) {
      Swal.fire({ icon: "warning", text: "Already Edited" });
      return;
    } else {
      if (checked) {
        setShowEditTable(false);
        setFieldValue("selectedCourses", [...values.selectedCourses, sno]);
      } else {
        setFieldValue(
          "selectedCourses",
          values.selectedCourses.filter((id) => id !== sno),
        );
      }
    }
  };

  /* EDIT */
  const handleEdit = () => {
    const selectedRows = courseData.filter((course) =>
      values.selectedCourses.includes(course.sno),
    );
    const data = selectedRows.map((c) => ({
      sno: c.sno,
      course_id: c.course_id,
      course_name: c.course_name,
      intake: c.intake,
      course_cat_id: c.course_cat_id,
      course_cat_name: c.course_cat_name,
      course_group_code: c.course_group_code,
      course_group_desc: c.course_group_desc,
      change_value: "",
      effective: "",
      aicte_ref: "",
    }));
    setFieldValue("intakeDetails", data);
    setShowEditTable(true);
    // simpler scroll
    const el = document.getElementById("editTable");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <FormikProvider value={formik}>
      <Form onChange={formik.handleChange} onSubmit={formik.handleSubmit}>
        <h5 className="mb-3 innerheader-new">
          <span>Course Intake Details</span>
        </h5>
        {/* FIRST TABLE */}
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead className="table-primary">
              <tr>
                <th>
                  {!enhanceStatus ? (
                    <>
                      <input
                        type="checkbox"
                        checked={
                          courseData.length > 0 &&
                          values.selectedCourses.length === courseData.length
                        }
                        onChange={handleSelectAll}
                      />{" "}
                      SL.No
                    </>
                  ) : (
                    "SL.No"
                  )}
                </th>
                <th>Academic Program Type</th>
                <th>Course / Program</th>
                <th>Branch / Specialization</th>
                <th>Medium</th>
                <th>Regular/Self Financed</th>
                <th>Permanent/Temporary</th>
                <th>Intake</th>
              </tr>
            </thead>
            <tbody>
              {courseData.length > 0 ? (
                courseData.map((course, index) => (
                  <tr key={course.sno}>
                    <td>
                      {!enhanceStatus ? (
                        <>
                          <input
                            type="checkbox"
                            checked={values.selectedCourses.includes(course.sno)}
                            onChange={(e) =>
                              handleRowSelect(course.sno, e.target.checked)
                            }
                          />{" "}
                          {index + 1}
                        </>
                      ) : (
                        index + 1
                      )}
                    </td>

                    <td>{course.course_group_desc}</td>
                    <td>{course.course_cat_name}</td>
                    <td>{course.course_name}</td>
                    <td>{course.medium_name}</td>
                    <td>
                      {course.reg_self === "S"
                        ? "Self Finance"
                        : course.reg_self === "R"
                          ? "Regular"
                          : ""}
                    </td>
                    <td>{course.course_noc_type}</td>
                    <td>{course.intake}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center text-danger fw-bold">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!enhanceStatus && (
          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleEdit}
              disabled={!values.selectedCourses.length}
            >
              Edit
            </button>
          </div>
        )}

        {/* SECOND TABLE */}
        {showEditTable && (
          <>
            <h5 className="mb-3 mt-3 innerheader-new">
              <span>Modified Course Intake Details</span>
            </h5>
            <FieldArray name="intakeDetails">
              {() => (
                <div id="editTable" className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead className="table-primary">
                      <tr className="small text-uppercase">
                        <th className="text-center">Sl.No</th>
                        <th>Academic Program Type</th>
                        <th>Course / Program </th>
                        <th>Course / Branch</th>
                        <th>Sanctioned Intake 25-26</th>
                        <th>Change Intake(+/-)</th>
                        <th>Effective Intake26-27</th>
                        <th>AICTE Ref.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {values.intakeDetails.map((row, index) => (
                        <tr key={row.sno}>
                          <td className="text-center">{index + 1}</td>
                          <td>{row.course_group_desc}</td>
                          <td>{row.course_cat_name}</td>
                          <td>{row.course_name}</td>
                          <td>
                            <Field
                              name={`intakeDetails[${index}].intake`}
                              className="form-control form-control-sm"
                              disabled
                            />
                            <ErrorMessage
                              name={`intakeDetails[${index}].intake`}
                              component="div"
                              className="text-danger small"
                            />
                          </td>
                          <td>
                            <Field
                              name={`intakeDetails[${index}].change_value`}
                              className="form-control form-control-sm"
                              disabled={enhanceStatus}
                            />
                            <ErrorMessage
                              name={`intakeDetails[${index}].change_value`}
                              component="div"
                              className="text-danger small"
                            />
                          </td>
                          <td>
                            <Field
                              name={`intakeDetails[${index}].effective`}
                              className="form-control form-control-sm"
                              disabled
                              value={
                                type === "enhance"
                                  ? Number(row.intake || 0) + Number(row.change_value || 0)
                                  : Number(row.intake || 0) - Number(row.change_value || 0)
                              }
                            />
                          </td>
                          <td>
                            <Field
                              name={`intakeDetails[${index}].aicte_ref`}
                              className="form-control form-control-sm"
                              disabled={enhanceStatus}
                            />
                            <ErrorMessage
                              name={`intakeDetails[${index}].aicte_ref`}
                              component="div"
                              className="text-danger small"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </FieldArray>
            {!enhanceStatus ? (
              <jnb.Row className="mt-4">
                <jnb.Col>
                  <div className="d-flex justify-content-end mt-4">
                    <button type="submit" className="btn btn-primary">
                      Submit
                    </button>
                  </div>
                </jnb.Col>
              </jnb.Row>
            ) : (
              <jnb.Row className="mt-4">
                <jnb.Col>
                  <div className="d-flex justify-content-end mt-4">
                    <span className="text-success fw-bold">
                      Already Submitted
                    </span>
                  </div>
                </jnb.Col>
              </jnb.Row>
            )}
          </>
        )}
      </Form>
    </FormikProvider>
  );
}
export default IntakeEnhancement;
